
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { setDataType, deletePrefix } from '@/views/editor/composable/useNote.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { deleteFormulaPositions, calcFunction } from '@/views/editor/composable/table/useFunction.ts'

export function hasSchemaInTable(table: HTMLTableElement): boolean {
	const allCells = table.querySelectorAll('td:not(.number--line)')
	for (const cell of allCells) {
		const td = cell as HTMLElement
		if (td.dataset.schemauid || td.dataset.schemaUid) {
			return true
		}
	}
	return false
}

export function clearSchemaFromTable(table: HTMLTableElement): void {
	const allCells = table.querySelectorAll('td:not(.number--line)')
	allCells.forEach((cell) => {
		const td = cell as HTMLElement

		if (td.dataset.schemauid || td.dataset.schemaUid) {
			delete td.dataset.schemauid
			delete td.dataset.schemaUid
			const div = td.querySelector('div')
			if (div) {
				div.setAttribute('contenteditable', 'true')
			}
		}

		if (td.dataset.type === 'schemaDropbox') {
			const keysToDelete = Object.keys(td.dataset)
			keysToDelete.forEach((key) => {
				delete td.dataset[key]
			})
			td.innerHTML = '<div contenteditable="true"></div>'
		}
	})
}

export function useDeleteCell() {
	function deleteCell(type: 'row' | 'column') {
		const { cellTarget, tableSelector, selector, noteType, noteUid, dragList } = storeToRefs(useEditorStore())
		const { socket } = storeToRefs(useCommonStore())
		const commonStore = useCommonStore()

		if (cellTarget.value.classList.contains('number--line')) {
			return
		}

		const table = cellTarget.value.closest('table')
		if (!table) {
			return
		}

			if (type === 'row' && hasSchemaInTable(table)) {
			const { lang } = storeToRefs(commonStore)
			commonStore.open(
				'confirm',
				lang.value.modal.title,
				lang.value.table.schemaDeleteConfirm,
				() => {
					performDeleteCell(type)
				}
			)
			return
		}

		performDeleteCell(type)
	}

	function performDeleteCell(type: 'row' | 'column') {
		const { cellTarget, tableSelector, selector, noteType, noteUid, dragList } = storeToRefs(useEditorStore())
		const { socket } = storeToRefs(useCommonStore())

		if (!cellTarget.value || cellTarget.value.classList.contains('number--line')) {
			return
		}

		const table = cellTarget.value.closest('table')
		if (!table) {
			return
		}

		useCreateTable().tableShapes(table)

		if (!tableSelector.value || tableSelector.value.length === 0) {
			return
		}

		let actualBlock = cellTarget.value.closest('.block') as HTMLElement
		
		if (!actualBlock) {
			let parent = cellTarget.value.parentElement
			let depth = 0
			while (parent && depth < 10) {
				if (parent.tagName !== 'TABLE' && parent.tagName !== 'TBODY' && parent.tagName !== 'TR' && 
				    parent.id && (parent.id.startsWith('temp-') || parent.id.startsWith('prefix-'))) {
					actualBlock = parent as HTMLElement
					break
				}
				parent = parent.parentElement
				depth++
			}
		}
		
		if (!actualBlock) {
			actualBlock = document.getElementById(selector.value.id) as HTMLElement
		}
		
		if (!actualBlock) {
			return
		}

		let targetRowIndex = -1
		let targetColIndex = -1

		tableSelector.value.forEach((rows: any, rowIndex: number) => {
			rows.forEach((cell: any, cellIndex: number) => {
				if (cell.html === cellTarget.value) {
					targetRowIndex = rowIndex
					targetColIndex = cellIndex
				}
			})
		})

		if (targetRowIndex === -1 || targetColIndex === -1) {
			return
		}

		const allTableRows = table.querySelectorAll('tr')
		const originalWidthArray: number[] = []
		const originalHeightArray: number[] = []

		const headerRow = allTableRows[0]
		if (headerRow) {
			const headerCells = headerRow.querySelectorAll('td.number--line')
			headerCells.forEach((cell: Element, index: number) => {
				if (index > 0) {
					const w = (cell as HTMLElement).style.width ? parseInt((cell as HTMLElement).style.width) : 120
					originalWidthArray.push(w)
				}
			})
		}

		for (let i = 1; i < allTableRows.length; i++) {
			const row = allTableRows[i] as HTMLTableRowElement
			const numberLine = row.cells[0] as HTMLElement
			const firstDataCell = row.querySelector('td:not(.number--line)') as HTMLTableCellElement
			if (firstDataCell) {
				if (firstDataCell.rowSpan > 1) {
					if (numberLine) {
						originalHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
					} else {
						originalHeightArray.push(40)
					}
				} else {
					const innerDiv = firstDataCell.querySelector('div') as HTMLElement
					if (innerDiv && innerDiv.style.height) {
						originalHeightArray.push(Math.round(parseFloat(innerDiv.style.height)))
					} else if (numberLine) {
						originalHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
					} else {
						originalHeightArray.push(40)
					}
				}
			} else if (numberLine) {
				originalHeightArray.push(Math.round(numberLine.getBoundingClientRect().height) - 1)
			} else {
				originalHeightArray.push(40)
			}
		}

		let colsToDelete: number[] = []
		let rowsToDelete: number[] = []

		if (type === 'row') {
			const targetCellHtml = cellTarget.value
			let mergedRowStart = targetRowIndex
			const mergedRowSpan = targetCellHtml.rowSpan || 1

			if (mergedRowSpan > 1) {
				for (let r = 0; r < tableSelector.value.length; r++) {
					if (tableSelector.value[r].some((cell: any) => cell.html === targetCellHtml)) {
						mergedRowStart = r
						break
					}
				}
			}

			for (let r = mergedRowStart; r < mergedRowStart + mergedRowSpan; r++) {
				rowsToDelete.push(r)
			}

			if (tableSelector.value.length - rowsToDelete.length < 1) {
				return
			}

			const adjustedRowCells = new Set<string>()
			rowsToDelete.forEach((rowIdx: number) => {
				tableSelector.value[rowIdx]?.forEach((cell: any) => {
					if (cell?.html && !cell.html.classList?.contains('number--line') && !adjustedRowCells.has(cell.html.id)) {
						if (cell.html.rowSpan > 1) {
							let cellOriginRow = rowIdx
							for (let r = 0; r < tableSelector.value.length; r++) {
								if (tableSelector.value[r].some((c: any) => c.html === cell.html)) {
									cellOriginRow = r
									break
								}
							}
							const cellRowEnd = cellOriginRow + cell.html.rowSpan - 1
							const overlapStart = Math.max(cellOriginRow, rowsToDelete[0])
							const overlapEnd = Math.min(cellRowEnd, rowsToDelete[rowsToDelete.length - 1])
							const overlapCount = overlapEnd - overlapStart + 1

							cell.html.rowSpan = Math.max(1, cell.html.rowSpan - overlapCount)
							adjustedRowCells.add(cell.html.id)
						}
					}
				})
			})

			for (let i = rowsToDelete.length - 1; i >= 0; i--) {
				tableSelector.value.splice(rowsToDelete[i], 1)
			}

			for (let i = rowsToDelete.length - 1; i >= 0; i--) {
				deleteFormulaPositions('row', rowsToDelete[i] + 1)
			}

		} else if (type === 'column') {
			const targetCellHtml = cellTarget.value
			let mergedColStart = targetColIndex
			const mergedColSpan = targetCellHtml.colSpan || 1

			if (mergedColSpan > 1) {
				for (let c = 0; c < tableSelector.value[targetRowIndex].length; c++) {
					if (tableSelector.value[targetRowIndex][c]?.html === targetCellHtml) {
						mergedColStart = c
						break
					}
				}
			}

			for (let c = mergedColStart; c < mergedColStart + mergedColSpan; c++) {
				colsToDelete.push(c)
			}

			const firstDataRow = tableSelector.value[0]
			const dataColCount = firstDataRow.filter((cell: any) => !cell.html?.classList?.contains('number--line')).length

			if (dataColCount - colsToDelete.length < 1) {
				return
			}

			const adjustedColCells = new Set<string>()
			tableSelector.value.forEach((rows: any) => {
				colsToDelete.forEach((colIdx: number) => {
					const cell = rows[colIdx]
					if (cell?.html && !cell.html.classList?.contains('number--line') && !adjustedColCells.has(cell.html.id)) {
						if (cell.html.colSpan > 1) {
							let cellOriginCol = colIdx
							for (let c = 0; c < rows.length; c++) {
								if (rows[c]?.html === cell.html) {
									cellOriginCol = c
									break
								}
							}
							const cellColEnd = cellOriginCol + cell.html.colSpan - 1
							const overlapStart = Math.max(cellOriginCol, colsToDelete[0])
							const overlapEnd = Math.min(cellColEnd, colsToDelete[colsToDelete.length - 1])
							const overlapCount = overlapEnd - overlapStart + 1

							cell.html.colSpan = Math.max(1, cell.html.colSpan - overlapCount)
							adjustedColCells.add(cell.html.id)
						}
					}
				})
			})

			for (let i = colsToDelete.length - 1; i >= 0; i--) {
				tableSelector.value.forEach((rows: any) => {
					rows.splice(colsToDelete[i], 1)
				})
			}

			for (let i = colsToDelete.length - 1; i >= 0; i--) {
				deleteFormulaPositions('col', colsToDelete[i] + 1)
			}
		}

		const widthArray: number[] = type === 'column'
			? originalWidthArray.filter((_: number, i: number) => !colsToDelete.includes(i))
			: [...originalWidthArray]

		const heightArray: number[] = type === 'row'
			? originalHeightArray.filter((_: number, i: number) => !rowsToDelete.includes(i))
			: [...originalHeightArray]

		const processedCellIds = new Set<string>()
		const rows: any = []

		tableSelector.value.forEach((rowList: any, rowIndex: number) => {
			const currentRow: any[] = []
			rowList.forEach((cell: any, cellIndex: number) => {
				if (cell.html && cell.html.classList && cell.html.classList.contains('number--line')) {
					return
				}
				
				if (!cell.html || typeof cell.html === 'string') {
					currentRow.push({
						cellUid: `temp-empty-${rowIndex}-${cellIndex}`,
						colSpan: 1,
						rowSpan: 1,
						content: '',
						dataType: {
							type: 'normal',
							colSpan: 1,
							rowSpan: 1
						}
					})
					return
				}
				
				const isMergedCell = (cell.html.rowSpan > 1) || (cell.html.colSpan > 1)
				if (isMergedCell && processedCellIds.has(cell.html.id)) {
					return
				}
				
				const dataType: any = setDataType(cell.html)
			
				dataType.colSpan = cell.html.colSpan || 1
				dataType.rowSpan = cell.html.rowSpan || 1

				if (rows.length === 0 && cellIndex === 0) {
					dataType.size = JSON.stringify({
						width: widthArray,
						height: heightArray
					})
				}

				let content = ''
				if (cell.html.dataset.type === 'function') {
					const spanEl = cell.html.querySelector('span')
					content = spanEl ? spanEl.innerHTML : ''
				} else if (cell.html.dataset.type === 'dropbox' || cell.html.dataset.type === 'schemaDropbox') {
					const dropboxContainer = cell.html.querySelector('.table--dropbox')
					const valueElement = dropboxContainer?.querySelector('.select--value p')
					content = valueElement ? valueElement.textContent || '' : ''
				} else {
					const divEl = cell.html.querySelector('div')
					content = divEl ? divEl.innerHTML : ''
				}

				currentRow.push({
					cellUid: deletePrefix(cell.html.id),
					colSpan: cell.html.colSpan || 1,
					rowSpan: cell.html.rowSpan || 1,
					content: content,
					dataType: dataType
				})

				if (isMergedCell) {
					processedCellIds.add(cell.html.id)
				}
			})
			
			rows.push(currentRow)
		})

		const blockUid = deletePrefix(actualBlock.id)
		const tableUid = deletePrefix(table.id)

		const params = {
			noteUID: noteUid.value,
			blockUID: blockUid,
			content: {
				type: 'defaultTable',
				tableUID: tableUid,
				cell: rows
			}
		}

		const tableContainer = actualBlock.querySelector('.editor--block__table-container')
		if (tableContainer) {
			tableContainer.remove()
		}

		const tableBlock = useCreateTable()
		const newTableHTML = tableBlock.insertTable(params.content, blockUid)
		actualBlock.innerHTML = newTableHTML

		tableBlock.event(actualBlock)
		const newTable = actualBlock.querySelector('table')
		if (newTable) {
			tableBlock.tableShapes(newTable)
			tableBlock.selector()
			tableBlock.widthSizeItem(newTable)
			tableBlock.heightSizeItem(newTable)
			calcFunction()
		}

		if (noteType.value === 'Project') {
			socket.value.createTableBlock(params)
		}

		const history = new UseHistory()
		history.push(false, { tableOperation: true })

		useDeleteTable().selector()
		
		dragList.value = []
		cellTarget.value = null
	}

	return {
		deleteCell,
	}
}
